import React, { useState } from 'react';
import { POI, UpdatePOIRequest } from '../services/poiService';
import { getAllCategories, getCategoryIdFromReadable } from '../utils/categoryUtils';

interface POIEditFormProps {
  poi: POI;
  onSubmit: (data: UpdatePOIRequest) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

const POIEditForm: React.FC<POIEditFormProps> = ({ poi, onSubmit, onCancel, isLoading = false }) => {
  const [formData, setFormData] = useState({
    name: poi.name || '',
    latitude: parseFloat(poi.latitude) || 0,
    longitude: parseFloat(poi.longitude) || 0,
    category: poi.category || '',
    city: poi.city || '',
    country: poi.country || ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [categories] = useState(() => getAllCategories());

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'POI name is required';
    } else if (formData.name.length > 255) {
      newErrors.name = 'POI name must be less than 255 characters';
    }
    
    if (!formData.category.trim()) {
      newErrors.category = 'Category is required';
    } else if (formData.category.length > 255) {
      newErrors.category = 'Category must be less than 255 characters';
    }
    
    if (formData.latitude < -90 || formData.latitude > 90) {
      newErrors.latitude = 'Latitude must be between -90 and 90';
    }
    
    if (formData.longitude < -180 || formData.longitude > 180) {
      newErrors.longitude = 'Longitude must be between -180 and 180';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      // Convert human-readable category to Google Places API identifier
      const submissionData = {
        ...formData,
        category: getCategoryIdFromReadable(formData.category)
      };
      await onSubmit(submissionData);
    } catch (error) {
      console.error('Error submitting POI edit form:', error);
    }
  };

  const handleInputChange = (field: keyof typeof formData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-h-[80vh] overflow-y-auto">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Edit Point of Interest</h3>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="edit-poi-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            POI Name *
          </label>
          <input
            id="edit-poi-name"
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className={`form-input ${errors.name ? 'border-red-500' : ''}`}
            placeholder="Enter the name of this location"
            disabled={isLoading}
          />
          {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
        </div>

        <div>
          <label htmlFor="edit-poi-category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Category *
          </label>
          <select
            id="edit-poi-category"
            value={formData.category}
            onChange={(e) => handleInputChange('category', e.target.value)}
            className={`form-input ${errors.category ? 'border-red-500' : ''}`}
            disabled={isLoading}
          >
            <option value="">Select a category</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.name}>
                {cat.name}
              </option>
            ))}
          </select>
          {errors.category && <p className="text-red-500 text-sm mt-1">{errors.category}</p>}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="edit-poi-city" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              City
            </label>
            <input
              id="edit-poi-city"
              type="text"
              value={formData.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              className="form-input"
              placeholder="Enter city name"
              disabled={isLoading}
            />
          </div>

          <div>
            <label htmlFor="edit-poi-country" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Country
            </label>
            <input
              id="edit-poi-country"
              type="text"
              value={formData.country}
              onChange={(e) => handleInputChange('country', e.target.value)}
              className="form-input"
              placeholder="Enter country name"
              disabled={isLoading}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="edit-poi-latitude" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Latitude
            </label>
            <input
              id="edit-poi-latitude"
              type="number"
              step="any"
              value={formData.latitude}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '' || val === '-') {
                  // Allow temporary empty or negative sign states while typing
                  return;
                }
                const parsed = parseFloat(val);
                if (!isNaN(parsed)) {
                  handleInputChange('latitude', parsed);
                }
              }}
              className={`form-input ${errors.latitude ? 'border-red-500' : ''}`}
              disabled={isLoading}
            />
            {errors.latitude && <p className="text-red-500 text-sm mt-1">{errors.latitude}</p>}
          </div>

          <div>
            <label htmlFor="edit-poi-longitude" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Longitude
            </label>
            <input
              id="edit-poi-longitude"
              type="number"
              step="any"
              value={formData.longitude}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '' || val === '-') {
                  // Allow temporary empty or negative sign states while typing
                  return;
                }
                const parsed = parseFloat(val);
                if (!isNaN(parsed)) {
                  handleInputChange('longitude', parsed);
                }
              }}
              className={`form-input ${errors.longitude ? 'border-red-500' : ''}`}
              disabled={isLoading}
            />
            {errors.longitude && <p className="text-red-500 text-sm mt-1">{errors.longitude}</p>}
          </div>
        </div>

        <div className="flex gap-3 justify-end pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="btn-secondary"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default POIEditForm;
